import Button from 'shared/ui/button/button';
import cls from './llms-insights.module.scss';
import { useTranslation } from 'hooks/api/useTranslation';
import { ICONS } from 'shared/constants/images';
import { Icon } from 'shared/ui/icon/icon';
import { IconTooltip } from 'shared/ui/icon-tooltip/icon-tooltip';
import { useState } from 'react';
import { Drawer } from 'shared/ui/drawer/drawer';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { Card } from 'shared/ui/card/card';
import { useCachedSelectedProject } from 'hooks';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import { ExternalLink } from 'shared/ui/external-link/external-link';
import {
  Extraction,
  Identified,
  InfoName,
} from 'widgets/score-disclosure/score-disclosuere.types';
import { getPagePrefix, track } from 'utils/mixpanel';
import { getTrackName } from 'widgets/score-disclosure/helpers';

export interface LLMSInsightsProps {
  title: InfoName;
  confidence?: string;
  evidence?: string;
  identified: Identified;
  urlsAnalyzed?: string[];
  extraction?: Extraction;
  isLoading?: boolean;
}

export const LLMSInsights: React.FC<LLMSInsightsProps> = (
  props: LLMSInsightsProps
) => {
  const {
    confidence,
    evidence,
    identified,
    urlsAnalyzed,
    extraction,
    title,
    isLoading,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { isTrial, isTrialExpired } = useCachedSelectedProject();
  const { t } = useTranslation();
  const trackName = getTrackName(title);
  const pagePrefix = getPagePrefix(window.location.pathname);
  const handleOpen = () => {
    setIsOpen(true);
    track(`AIS:${pagePrefix}_LLM_INSIGHTS-SECTION-OPENED`, {
      is_trial: isTrial,
      type: trackName,
    });
  };
  const handleClose = () => {
    setIsOpen(false);
    track(`AIS:${pagePrefix}_LLM_INSIGHTS-SECTION-CLOSED`, {
      type: trackName,
    });
  };
  // REFACTOR: move track functions outside of component to avoid re-creating them on each render
  const trackDisclosureClick = (cardType: string, open: boolean) => {
    track(
      !open
        ? `AIS:${pagePrefix}_CRD_ANALYSIS_SUBSECTION-EXPANDED`
        : `AIS:${pagePrefix}_CRD_ANALYSIS_SUBSECTION-COLAPSED`,
      {
        is_trial: isTrial,
        type: trackName,
        card_type: cardType,
      }
    );
  };

  const handleLinkTrack = (link: string) => {
    track(`AIS:${pagePrefix}_LLM_INSIGHTS_SUBSECTION-LINK-CLICKED`, {
      is_trial: isTrial,
      type: trackName,
      link: link,
    });
  };
  return (
    <>
      <div className={cls.title}>
        {isLoading ? (
          <Skeleton width={175} height={20} />
        ) : (
          <Button
            onClick={handleOpen}
            theme='text'
            tone='default'
            disabled={isTrialExpired}
            className={cls.btn}
          >
            {t('llmsInsights')}
          </Button>
        )}
        <div className={cls.stats}>
          {isLoading ? (
            <Skeleton width={218} height={16} />
          ) : (
            <p>
              {t('confidence')}:{' '}
              <b>
                {isTrialExpired ? (
                  <Icon pathToIcon={ICONS.lockGray} size={16} />
                ) : (
                  confidence
                )}
              </b>{' '}
              <b>•</b>
              {t('evidence')}:{' '}
              <b>
                {isTrialExpired ? (
                  <Icon pathToIcon={ICONS.lockGray} size={16} />
                ) : (
                  evidence
                )}
              </b>
            </p>
          )}
          <IconTooltip
            id={`confidence-tt-${title}`}
            iconSize={16}
            onMouseEnter={() =>
              track(
                `AIS:${pagePrefix}_INFO_COMPLETENESS-SECTION_TOOLTIP-HOVER`,
                {
                  is_trial: isTrial,
                  type: trackName,
                }
              )
            }
          >
            <span>
              <b>{t('confidence')}</b>: {t('confidenceTooltip')}
            </span>
            <br />
            <br />
            <span>
              <b>{t('evidence')}</b>: {t('evidenceTooltip')}
            </span>
          </IconTooltip>
        </div>
      </div>
      <Drawer isOpen={isOpen} onClose={handleClose} title={t('llmsInsights')}>
        <div className={cls.drawer}>
          <Disclosure defaultOpen>
            {({ open }) => (
              <Card className={cls.discCard}>
                <DisclosureButton
                  className={cls.disclosureButton}
                  onClick={() => trackDisclosureClick('Analysis Context', open)}
                >
                  <h4>{t('analysisContext')}</h4>
                  <div className={cls.right}>
                    <Icon
                      pathToIcon={ICONS.arrowUp}
                      size={20}
                      className={`${cls.arrow} ${open ? cls.open : ''}`}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className={`${cls.panel} ${cls.flex}`}>
                  <Card className={cls.card}>
                    <h2>{t('analysedURLs')}</h2>
                    {urlsAnalyzed &&
                      urlsAnalyzed.map(url => (
                        <ExternalLink
                          key={url}
                          href={url}
                          onClick={() => handleLinkTrack(url)}
                        >
                          <Icon pathToIcon={ICONS.link} size={16} />
                          <span>{url}</span>
                        </ExternalLink>
                      ))}
                  </Card>
                  <Card className={`${cls.card} ${cls.col}`}>
                    <div>
                      <h2>{t(`${title}Identified`)}</h2>
                      <p>{identified.primary}</p>
                    </div>
                    <div className={cls.stats}>
                      <p>
                        {t('confidence')}: <b>{identified.confidence_level}</b>{' '}
                        • {t('evidence')}: <b>{identified.evidence_strength}</b>
                      </p>
                    </div>
                  </Card>
                </DisclosurePanel>
              </Card>
            )}
          </Disclosure>
          {extraction?.explicit_statements &&
            extraction?.explicit_statements.length > 0 && (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <Card className={cls.discCard}>
                    <DisclosureButton
                      className={cls.disclosureButton}
                      onClick={() =>
                        trackDisclosureClick('Explicit Statements', open)
                      }
                    >
                      <h4>{t('explicitStatements')}</h4>
                      <div className={cls.right}>
                        <Icon
                          pathToIcon={ICONS.arrowUp}
                          size={20}
                          className={`${cls.arrow} ${open ? cls.open : ''}`}
                        />
                      </div>
                    </DisclosureButton>
                    <DisclosurePanel className={cls.panel}>
                      {extraction?.explicit_statements.map(s => (
                        <Card className={cls.card} key={s.page_url}>
                          <h3>{s.context}</h3>
                          <p className={cls.mb16}>{s.quote}</p>
                          <ExternalLink href={s.page_url}>
                            <Icon pathToIcon={ICONS.link} size={16} />
                            {s.page_url}
                          </ExternalLink>
                        </Card>
                      ))}
                    </DisclosurePanel>
                  </Card>
                )}
              </Disclosure>
            )}
          {extraction?.implied_indicators &&
            extraction?.implied_indicators.length > 0 && (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <Card className={cls.discCard}>
                    <DisclosureButton
                      className={cls.disclosureButton}
                      onClick={() =>
                        trackDisclosureClick('Implied Indicators', open)
                      }
                    >
                      <h4>{t('impliedIndicators')}</h4>
                      <div className={cls.right}>
                        <Icon
                          pathToIcon={ICONS.arrowUp}
                          size={20}
                          className={`${cls.arrow} ${open ? cls.open : ''}`}
                        />
                      </div>
                    </DisclosureButton>
                    <DisclosurePanel className={cls.panel}>
                      {extraction?.implied_indicators.map(i => (
                        <Card className={cls.card} key={i.page_url}>
                          <h3>{i.signal}</h3>
                          <p className={cls.mb16}>{i.reasoning}</p>
                          <ExternalLink href={i.page_url}>
                            <Icon pathToIcon={ICONS.link} size={16} />
                            {i.page_url}
                          </ExternalLink>
                        </Card>
                      ))}
                    </DisclosurePanel>
                  </Card>
                )}
              </Disclosure>
            )}
        </div>
      </Drawer>
    </>
  );
};
